import { environment } from '../../../environments/environment';

export interface SoapField {
  key: string;
  label: string;
  required?: boolean;
  placeholder?: string;
  type?: 'text' | 'password' | 'number' | 'textarea' | string;
  xsdType?: 'string' | 'int';
  hint?: string;
  credential?: boolean;
  /** Feldnamen der CSV-Zeile laut Schnittstellenbeschreibung (nur Anzeige) */
  csvStructure?: string;
}

export type SoapEnvelopeStyle = 'document' | 'rpc' | 'logistikportal';
export type SoapOperationDirection = 'read' | 'write';

export interface SoapOperation {
  id: string;
  label: string;
  description: string;
  method: string;
  soapAction: string;
  envelopeStyle?: SoapEnvelopeStyle;
  fields: SoapField[];
  signature?: string;
  source?: 'preset' | 'wsdl';
  /** Lesen oder Schreiben ins Logistikportal */
  direction?: SoapOperationDirection;
  /** Schritt beim Termin-Schreiben (setTouren = 1, setRollkarte = 2) */
  terminWriteStep?: 1 | 2;
}

/** CSV-Feldaufbau und Vorbelegung laut Webservice-Dokumentation 2015 */
export const ROLLER_CSV_SPECS: Record<string, { structure: string; example: string; hint: string }> = {
  setPLZGebietszuordnung: {
    structure: 'PLZ;Gebietsname',
    example: '45879;Norden\n45881;Westen\n45881;Norden\n45883;Süden',
    hint: 'Semikolongetrennt, Zeilen durch Zeilenumbruch. Sonderzeichen UTF-8.',
  },
  setKontingente: {
    structure: 'Kalendertag;AnzahlAuftraege;AnzahlME;AnzahlRekla;Kontingentart',
    example: '2012-02-21;10;200;3;ganztags',
    hint: 'Kalendertag YYYY-MM-DD (Zukunft). Kontingentart optional: ganztags, vormittags, nachmittags. Löschen: 0;0;0',
  },
  setTouren: {
    structure: 'Kaufvertragsnummer;Kaufvertragsposition;TourSchluessel;LKWSchluessel;MonteurSchluessel',
    example: '03A1B2;1;030;2;3',
    hint: 'Termin-Schreiben Schritt 1: Tour-/LKW-/Monteur-Zuordnung. TourSchluessel, LKWSchluessel, MonteurSchluessel müssen enthalten sein, dürfen leer sein (Löschen). Kalendertag muss in der Zukunft liegen.',
  },
  setRollkarte: {
    structure: 'BasisKaufvertragsnummer;TourSchluessel;ZeitraumVon;ZeitraumBis;RollkartenPosition',
    example: '03A1B2;030;8;10;1',
    hint: 'Termin-Schreiben Schritt 2: Lieferzeitfenster und Rollkarten-Position. ZeitraumVon/ZeitraumBis als Dezimalstunde (z. B. 8.5 = 08:30), ZeitraumVon < ZeitraumBis.',
  },
};

const TERMIN_WRITE_DEFAULTS = {
  branchNo: '78',
  calendarDay: '20260715',
};

export interface WsdlOperationDto {
  id: string;
  label: string;
  description: string;
  method: string;
  soapAction?: string;
  envelopeStyle?: SoapEnvelopeStyle;
  signature?: string;
  source?: 'wsdl';
  fields: SoapField[];
}

export interface SoapExecutePayload {
  endpoint?: string;
  soapAction?: string;
  envelope?: string;
  method?: string;
  parameters?: Record<string, string>;
}

export interface SoapInterpretedResponse {
  parseStatus?: string;
  responseMethod?: string | null;
  portalSuccess?: boolean | null;
  portalError?: { code?: string | number | null; message?: string | null } | null;
  auftraegeCount?: number | null;
  importStats?: { linesCorrect?: number | null; linesIncorrct?: number | null } | null;
  hints?: string[];
}

export interface SoapExecuteResult {
  success: boolean;
  httpCode?: number;
  durationMs?: number;
  raw?: string;
  parsed?: unknown;
  interpreted?: SoapInterpretedResponse;
  fault?: unknown;
  error?: string;
  request?: {
    endpoint: string;
    soapAction?: string;
    envelope: string;
  };
}

export interface SoapHistoryEntry {
  id: string;
  label: string;
  timestamp: string;
  success: boolean;
  durationMs?: number;
  payload: SoapExecutePayload;
  result?: SoapExecuteResult;
}

export interface CustomParamRow {
  key: string;
  value: string;
}

const credentials = environment.soapService.credentials;
const endpoint = environment.soapService.wsdlUrl;

const CREDENTIAL_KEYS = new Set(['userLogin', 'userPassword', 'vendorNo', 'user', 'password']);
const MANDATORY_PARAM_KEYS = ['userLogin', 'userPassword', 'vendorNo'];

const AUTH_FIELDS: SoapField[] = [
  { key: 'userLogin', label: 'userLogin (Logistikportal-Benutzername)', required: true, credential: true },
  { key: 'userPassword', label: 'userPassword (Logistikportal-Passwort)', required: true, type: 'password', credential: true },
  {
    key: 'vendorNo',
    label: 'vendorNo (Roller Kreditorennr. der Spedition)',
    required: true,
    credential: true,
    placeholder: 'z. B. 999888182'
  }
];

function getCredentialValue(key: string): string {
  const creds = credentials as Record<string, string>;
  return creds[key] || '';
}

export function ensureMandatorySoapParams(params: Record<string, string>): Record<string, string> {
  const result = { ...params };

  MANDATORY_PARAM_KEYS.forEach((key) => {
    const value = getCredentialValue(key);
    if (value && !String(result[key] ?? '').trim()) {
      result[key] = value;
    }
  });

  return result;
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

const PLAIN_TEXT_PARAM_KEYS = new Set(['userPassword', 'password']);

function formatSoapParamValue(key: string, value: string): string {
  if (PLAIN_TEXT_PARAM_KEYS.has(key)) {
    return String(value);
  }

  return escapeXml(String(value));
}

export function buildDocumentEnvelope(method: string, params: Record<string, string>): string {
  const paramXml = Object.entries(params)
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([key, value]) => `<${key}>${formatSoapParamValue(key, String(value))}</${key}>`)
    .join('');

  return `<?xml version="1.0" encoding="UTF-8"?>
<SOAP-ENV:Envelope xmlns:SOAP-ENV="http://schemas.xmlsoap.org/soap/envelope/">
  <SOAP-ENV:Body>
    <${method} xmlns="${endpoint}">
      ${paramXml}
    </${method}>
  </SOAP-ENV:Body>
</SOAP-ENV:Envelope>`;
}

export function buildRpcEnvelope(method: string, params: Record<string, string>): string {
  const paramXml = Object.entries(params)
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([key, value]) => `<${key} xsi:type="xsd:string">${formatSoapParamValue(key, String(value))}</${key}>`)
    .join('\n      ');

  return `<?xml version="1.0" encoding="UTF-8"?>
<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/"
                  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
                  xmlns:xsd="http://www.w3.org/2001/XMLSchema">
  <soapenv:Body>
    <${method} soapenv:encodingStyle="http://schemas.xmlsoap.org/soap/encoding/">
      ${paramXml}
    </${method}>
  </soapenv:Body>
</soapenv:Envelope>`;
}

function resolveXsdType(field: SoapField | undefined): string {
  return field?.xsdType === 'int' ? 'xsd:int' : 'xsd:string';
}

export function buildLogistikportalEnvelope(
  method: string,
  params: Record<string, string>,
  fields: SoapField[] = []
): string {
  const orderedKeys: Array<{ key: string; xsdType: string }> = [];
  const used = new Set<string>();

  fields.forEach((field) => {
    orderedKeys.push({ key: field.key, xsdType: resolveXsdType(field) });
    used.add(field.key);
  });

  Object.keys(params).forEach((key) => {
    if (!used.has(key)) {
      orderedKeys.push({ key, xsdType: 'xsd:string' });
    }
  });

  const paramXml = orderedKeys
    .map(({ key, xsdType }) => {
      const value = params[key] ?? '';
      return `<${key} xsi:type="${xsdType}">${formatSoapParamValue(key, String(value))}</${key}>`;
    })
    .join('\n            ');

  return `<soapenv:Envelope
    xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
    xmlns:xsd="http://www.w3.org/2001/XMLSchema"
    xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/"
    xmlns:urn="urn:Logistikportal">

    <soapenv:Header/>

    <soapenv:Body>
        <urn:${method} soapenv:encodingStyle="http://schemas.xmlsoap.org/soap/encoding/">
            ${paramXml}
        </urn:${method}>
    </soapenv:Body>

</soapenv:Envelope>`;
}

export function formatXml(xml: string): string {
  if (!xml) {
    return '';
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xml, 'application/xml');
    if (doc.getElementsByTagName('parsererror').length) {
      return xml;
    }

    const serialized = new XMLSerializer().serializeToString(doc);
    const lines = serialized.replace(/>\s*</g, '>\n<').split('\n');
    let indent = 0;
    return lines
      .map((line) => {
        const trimmed = line.trim();
        if (!trimmed) {
          return '';
        }
        if (trimmed.startsWith('</')) {
          indent = Math.max(indent - 1, 0);
        }
        const padded = `${'  '.repeat(indent)}${trimmed}`;
        if (trimmed.startsWith('<') && !trimmed.startsWith('</') && !trimmed.endsWith('/>') && !trimmed.includes('</')) {
          indent += 1;
        }
        return padded;
      })
      .filter(Boolean)
      .join('\n');
  } catch {
    return xml;
  }
}

export function isWriteOperation(operation: SoapOperation): boolean {
  return operation.direction === 'write';
}

export function isTerminWriteOperation(operation: SoapOperation): boolean {
  return operation.terminWriteStep === 1 || operation.terminWriteStep === 2;
}

export const ROLLER_SOAP_OPERATIONS: SoapOperation[] = [
  {
    id: 'getTouren',
    label: 'getTouren (Lesen)',
    description: 'Alle gebuchten Aufträge eines Tages für Filiale und Spediteur abrufen (Response: LogistikportalTouren).',
    method: 'getTouren',
    soapAction: '',
    envelopeStyle: 'logistikportal',
    source: 'preset',
    direction: 'read',
    fields: [
      ...AUTH_FIELDS,
      {
        key: 'branchNo',
        label: 'branchNo (Roller Filialnummer, 2-stellig)',
        placeholder: 'z. B. 70',
        hint: 'Pflicht zusammen mit calendarDay – oder stattdessen kvNo'
      },
      {
        key: 'calendarDay',
        label: 'calendarDay (Integer, YYYYMMDD)',
        xsdType: 'int',
        placeholder: 'z. B. 20260407',
        hint: 'Pflicht zusammen mit branchNo – oder stattdessen kvNo'
      },
      {
        key: 'kvNo',
        label: 'kvNo (Kaufvertragsnummer, 6-stellig)',
        placeholder: 'z. B. 9ANEAF',
        hint: 'Alternative zu branchNo/calendarDay'
      }
    ]
  },
  {
    id: 'setPLZGebietszuordnung',
    label: 'setPLZGebietszuordnung (Schreiben)',
    description: 'PLZ-Gebietszuordnungen für Filiale und Spediteur importieren (Response: LogistikportalImportProtocol).',
    method: 'setPLZGebietszuordnung',
    soapAction: '',
    envelopeStyle: 'logistikportal',
    source: 'preset',
    direction: 'write',
    fields: [
      ...AUTH_FIELDS,
      { key: 'branchNo', label: 'branchNo (Roller Filialnummer, 2-stellig)', required: true, placeholder: 'z. B. 78' },
      {
        key: 'csvdata',
        label: 'csvdata',
        required: true,
        type: 'textarea',
        csvStructure: ROLLER_CSV_SPECS.setPLZGebietszuordnung.structure,
        placeholder: ROLLER_CSV_SPECS.setPLZGebietszuordnung.example,
        hint: ROLLER_CSV_SPECS.setPLZGebietszuordnung.hint,
      }
    ]
  },
  {
    id: 'setKontingente',
    label: 'setKontingente (Schreiben)',
    description: 'Kapazitäten zu einem Kontingent anlegen, bearbeiten und löschen (Response: LogistikportalImportProtocol).',
    method: 'setKontingente',
    soapAction: '',
    envelopeStyle: 'logistikportal',
    source: 'preset',
    direction: 'write',
    fields: [
      ...AUTH_FIELDS,
      { key: 'branchNo', label: 'branchNo (Roller Filialnummer, 2-stellig)', required: true, placeholder: 'z. B. 78' },
      { key: 'quota', label: 'quota (Kontingentname)', required: true, placeholder: 'z. B. Möbel' },
      {
        key: 'csvdata',
        label: 'csvdata',
        required: true,
        type: 'textarea',
        csvStructure: ROLLER_CSV_SPECS.setKontingente.structure,
        placeholder: ROLLER_CSV_SPECS.setKontingente.example,
        hint: ROLLER_CSV_SPECS.setKontingente.hint,
      }
    ]
  },
  {
    id: 'setTouren',
    label: 'Termin schreiben – setTouren (Schritt 1)',
    description: 'Termin ins Logistikportal schreiben: Tour-, LKW- und Monteur-Zuordnung am Kalendertag (Response: LogistikportalImportProtocol).',
    method: 'setTouren',
    soapAction: '',
    envelopeStyle: 'logistikportal',
    source: 'preset',
    direction: 'write',
    terminWriteStep: 1,
    fields: [
      ...AUTH_FIELDS,
      { key: 'branchNo', label: 'branchNo (Roller Filialnummer, 2-stellig)', required: true, placeholder: 'z. B. 78' },
      { key: 'calendarDay', label: 'calendarDay (String, YYYYMMDD)', required: true, placeholder: 'z. B. 20260715' },
      {
        key: 'csvdata',
        label: 'csvdata',
        required: true,
        type: 'textarea',
        csvStructure: ROLLER_CSV_SPECS.setTouren.structure,
        placeholder: ROLLER_CSV_SPECS.setTouren.example,
        hint: ROLLER_CSV_SPECS.setTouren.hint,
      }
    ]
  },
  {
    id: 'setRollkarte',
    label: 'Termin schreiben – setRollkarte (Schritt 2)',
    description: 'Termin ins Logistikportal schreiben: Lieferzeitfenster und Rollkarten-Position (Response: LogistikportalImportProtocol).',
    method: 'setRollkarte',
    soapAction: '',
    envelopeStyle: 'logistikportal',
    source: 'preset',
    direction: 'write',
    terminWriteStep: 2,
    fields: [
      ...AUTH_FIELDS,
      { key: 'branchNo', label: 'branchNo (Roller Filialnummer, 2-stellig)', required: true, placeholder: 'z. B. 78' },
      { key: 'calendarDay', label: 'calendarDay (String, YYYYMMDD)', required: true, placeholder: 'z. B. 20260715' },
      {
        key: 'csvdata',
        label: 'csvdata',
        required: true,
        type: 'textarea',
        csvStructure: ROLLER_CSV_SPECS.setRollkarte.structure,
        placeholder: ROLLER_CSV_SPECS.setRollkarte.example,
        hint: ROLLER_CSV_SPECS.setRollkarte.hint,
      }
    ]
  },
  {
    id: 'validateUser',
    label: 'validateUser',
    description: 'Benutzeranmeldung am ROLLER-Logistikportal prüfen (WSDL).',
    method: 'validateUser',
    soapAction: '',
    envelopeStyle: 'rpc',
    source: 'preset',
    fields: [
      { key: 'userLogin', label: 'userLogin', required: true, credential: true },
      { key: 'userPassword', label: 'userPassword', required: true, type: 'password', credential: true },
      { key: 'user', label: 'user', required: true, placeholder: 'SOAPmh3' },
      { key: 'password', label: 'password', required: true, type: 'password' }
    ]
  }
];

export function mapWsdlOperation(dto: WsdlOperationDto): SoapOperation {
  const preset = ROLLER_SOAP_OPERATIONS.find((op) => op.method === dto.method);

  return {
    id: dto.id || dto.method,
    label: dto.label || dto.method,
    description: preset?.description || dto.description || dto.signature || dto.method,
    method: dto.method,
    soapAction: dto.soapAction || '',
    envelopeStyle: dto.envelopeStyle || preset?.envelopeStyle || 'logistikportal',
    signature: dto.signature,
    source: 'wsdl',
    fields: (dto.fields?.length ? dto.fields : preset?.fields || []).map((field) => ({
      ...field,
      type: field.type === 'password' ? 'password' : field.type === 'textarea' ? 'textarea' : 'text',
      credential: CREDENTIAL_KEYS.has(field.key),
    })),
  };
}

export function mergeWsdlOperations(wsdlOperations: WsdlOperationDto[]): SoapOperation[] {
  if (!wsdlOperations.length) {
    return [...ROLLER_SOAP_OPERATIONS];
  }

  const result: SoapOperation[] = wsdlOperations.map((dto) => {
    const preset = ROLLER_SOAP_OPERATIONS.find((op) => op.method === dto.method);
    const mapped = mapWsdlOperation(dto);

    if (!preset) {
      return mapped;
    }

    return {
      ...mapped,
      description: preset.description,
      envelopeStyle: preset.envelopeStyle || mapped.envelopeStyle,
      fields: preset.fields,
      direction: preset.direction,
      terminWriteStep: preset.terminWriteStep,
      label: preset.label,
    };
  });

  const wsdlMethods = new Set(result.map((op) => op.method));
  ROLLER_SOAP_OPERATIONS.forEach((preset) => {
    if (!wsdlMethods.has(preset.method)) {
      result.push(preset);
    }
  });

  return result;
}

export function isPresetOperation(operation: SoapOperation): boolean {
  return ROLLER_SOAP_OPERATIONS.some((preset) => preset.method === operation.method);
}

function applyCsvDefaults(operation: SoapOperation, params: Record<string, string>): void {
  const spec = ROLLER_CSV_SPECS[operation.method];
  if (spec && !String(params.csvdata ?? '').trim()) {
    params.csvdata = spec.example;
  }

  if (operation.method === 'setTouren' || operation.method === 'setRollkarte') {
    if (!String(params.branchNo ?? '').trim()) {
      params.branchNo = TERMIN_WRITE_DEFAULTS.branchNo;
    }
    if (!String(params.calendarDay ?? '').trim()) {
      params.calendarDay = TERMIN_WRITE_DEFAULTS.calendarDay;
    }
  }

  if (operation.method === 'setPLZGebietszuordnung' && !String(params.branchNo ?? '').trim()) {
    params.branchNo = TERMIN_WRITE_DEFAULTS.branchNo;
  }

  if (operation.method === 'setKontingente') {
    if (!String(params.branchNo ?? '').trim()) {
      params.branchNo = TERMIN_WRITE_DEFAULTS.branchNo;
    }
    if (!String(params.quota ?? '').trim()) {
      params.quota = 'Möbel';
    }
  }
}

export function getDefaultParams(operation: SoapOperation, customParams: CustomParamRow[] = []): Record<string, string> {
  const params: Record<string, string> = {};

  operation.fields.forEach((field) => {
    if (field.credential) {
      params[field.key] = getCredentialValue(field.key);
      return;
    }

    if (field.key === 'user') {
      params[field.key] = credentials.userLogin;
      return;
    }

    if (field.key === 'password') {
      params[field.key] = credentials.userPassword;
      return;
    }

    params[field.key] = '';
  });

  if (operation.method === 'getTouren') {
    if (!String(params.branchNo ?? '').trim()) {
      params.branchNo = '70';
    }
    if (!String(params.calendarDay ?? '').trim()) {
      params.calendarDay = '20260407';
    }
  }

  applyCsvDefaults(operation, params);

  customParams.forEach((row) => {
    const key = row.key.trim();
    if (key) {
      params[key] = row.value;
    }
  });

  return ensureMandatorySoapParams(params);
}

export function collectParams(
  operation: SoapOperation,
  baseParams: Record<string, string>,
  customParams: CustomParamRow[]
): Record<string, string> {
  const params = { ...baseParams };

  customParams.forEach((row) => {
    const key = row.key.trim();
    if (key) {
      params[key] = row.value;
    }
  });

  return ensureMandatorySoapParams(params);
}

export function validateOperationParams(operation: SoapOperation, params: Record<string, string>): string | null {
  const missing = operation.fields
    .filter((field) => field.required && !String(params[field.key] ?? '').trim())
    .map((field) => field.label);

  if (missing.length) {
    return `Pflichtfelder fehlen: ${missing.join(', ')}`;
  }

  if (operation.method === 'getTouren') {
    const hasBranchDay = String(params.branchNo ?? '').trim() && String(params.calendarDay ?? '').trim();
    const hasKvNo = String(params.kvNo ?? '').trim();

    if (!hasBranchDay && !hasKvNo) {
      return 'getTouren: branchNo + calendarDay oder kvNo erforderlich';
    }
  }

  return null;
}

export function buildEnvelopeForOperation(operation: SoapOperation, params: Record<string, string>): string {
  const payload = ensureMandatorySoapParams(params);

  if (operation.envelopeStyle === 'rpc') {
    return buildRpcEnvelope(operation.method, payload);
  }

  if (operation.envelopeStyle === 'logistikportal') {
    return buildLogistikportalEnvelope(operation.method, payload, operation.fields);
  }

  return buildDocumentEnvelope(operation.method, payload);
}

export interface PreparedSoapOperationRequest {
  operation: SoapOperation;
  params: Record<string, string>;
  envelope: string;
  validationError: string | null;
}

export function prepareSoapOperationRequest(
  method: string,
  overrides: Record<string, string> = {},
  customParams: CustomParamRow[] = []
): PreparedSoapOperationRequest | null {
  const operation = ROLLER_SOAP_OPERATIONS.find((entry) => entry.method === method);
  if (!operation) {
    return null;
  }

  const baseParams = {
    ...getDefaultParams(operation),
    ...overrides,
  };
  const params = collectParams(operation, baseParams, customParams);
  const validationError = validateOperationParams(operation, params);
  const envelope = buildEnvelopeForOperation(operation, params);

  return { operation, params, envelope, validationError };
}

function xmlLocalName(node: Element): string {
  return node.localName || node.tagName.replace(/^[^:]+:/, '');
}

function isMeaningfulOrderItemElement(item: Element): boolean {
  const fields: Record<string, string> = {};
  for (let index = 0; index < item.children.length; index++) {
    const child = item.children[index];
    const name = xmlLocalName(child);
    if (!name || name === 'item') {
      continue;
    }
    fields[name] = (child.textContent || '').trim();
  }

  return !!(
    fields.Kaufvertragsnummer
    || fields.Vorname
    || fields.Nachname
    || fields.Filiale
    || fields.Strasse
    || fields.PLZ
  );
}

function collectOrderItemsFromContainer(container: Element): Element[] {
  return Array.from(container.children).filter((child) => xmlLocalName(child) === 'item');
}

/** Prüft im SOAP-Body, ob unter auftraege (oder items) echte item-Einträge mit Auftragsdaten liegen. */
export function soapResponseHasItemElement(raw?: string | null): boolean {
  if (!raw?.trim()) {
    return false;
  }

  try {
    const doc = new DOMParser().parseFromString(raw, 'text/xml');
    if (doc.getElementsByTagName('parsererror').length) {
      return /<item[\s>/]/i.test(raw);
    }

    const body = Array.from(doc.getElementsByTagName('*')).find((node) => xmlLocalName(node) === 'Body');
    const searchRoots = body ? [body] : [doc.documentElement].filter(Boolean) as Element[];

    for (const root of searchRoots) {
      const containers = Array.from(root.getElementsByTagName('*')).filter((node) => {
        const name = xmlLocalName(node);
        return name === 'auftraege' || name === 'items';
      });

      for (const container of containers) {
        const items = collectOrderItemsFromContainer(container);
        if (items.some(isMeaningfulOrderItemElement)) {
          return true;
        }
      }

      const bodyItems = Array.from(root.getElementsByTagName('*')).filter((node) => xmlLocalName(node) === 'item');
      if (bodyItems.some(isMeaningfulOrderItemElement)) {
        return true;
      }
    }

    return false;
  } catch {
    return /<item[\s>/]/i.test(raw);
  }
}

function parsedValueHasOrderItems(value: unknown): boolean {
  if (value == null) {
    return false;
  }

  if (Array.isArray(value)) {
    return value.some((entry) => parsedValueHasOrderItems(entry));
  }

  if (typeof value !== 'object') {
    return false;
  }

  const record = value as Record<string, unknown>;
  if (record.Kaufvertragsnummer || record.Vorname || record.Nachname || record.Filiale) {
    return true;
  }

  if (record.item != null) {
    return parsedValueHasOrderItems(record.item);
  }

  if (record.auftraege != null) {
    return parsedValueHasOrderItems(record.auftraege);
  }

  return false;
}

/** SOAP-Antwort enthält verwertbare Auftrags-items (Body-XML, parsed oder auftraegeCount). */
export function soapResponseHasOrderItems(result: SoapExecuteResult | null | undefined): boolean {
  if (!result) {
    return false;
  }

  const count = result.interpreted?.auftraegeCount;
  if (count != null && count > 0) {
    return true;
  }

  if (soapResponseHasItemElement(result.raw)) {
    return true;
  }

  return parsedValueHasOrderItems(result.parsed);
}

export interface SoapResponseFeedback {
  code: string;
  message: string;
  httpCode?: number;
  portalSuccess?: boolean | null;
  auftraegeCount?: number | null;
  hints?: string[];
}

/** Roller/Logistikportal: 200 / OK / 0 = Erfolg (kein Fehler). */
export function isRollerPortalSuccessCode(code: unknown): boolean {
  if (code == null || code === '') {
    return false;
  }
  const normalized = String(code).trim().toUpperCase();
  return normalized === '200' || normalized === 'OK' || normalized === '0';
}

/** setTouren / SOAP-Aufruf erfolgreich? Code 200 zählt immer als OK. */
export function isSoapPortalCallSuccessful(
  result: SoapExecuteResult | null | undefined,
  feedback?: SoapResponseFeedback | null
): boolean {
  if (!result) {
    return false;
  }
  if (result.fault) {
    return false;
  }

  const fb = feedback ?? formatSoapResponseFeedback(result);
  if (isRollerPortalSuccessCode(fb.code) || isRollerPortalSuccessCode(fb.httpCode) || isRollerPortalSuccessCode(result.httpCode)) {
    return true;
  }
  if (fb.portalSuccess === true) {
    return true;
  }
  if (fb.portalSuccess === false) {
    return false;
  }
  if (result.error && result.success === false) {
    return false;
  }
  return result.success !== false;
}

/** Portal-/HTTP-Code und Meldung aus SOAP-Antwort (wie Stats → Roller API). */
export function formatSoapResponseFeedback(result: SoapExecuteResult | null | undefined): SoapResponseFeedback {
  if (!result) {
    return { code: '?', message: 'Keine SOAP-Antwort' };
  }

  const interpreted = result.interpreted;
  const httpCode = result.httpCode;
  const hints = interpreted?.hints?.filter(Boolean) ?? [];

  if (result.fault) {
    const fault = result.fault as Record<string, unknown>;
    return {
      code: String(fault?.faultcode ?? httpCode ?? 'SOAP-Fault'),
      message: getSoapExecuteErrorMessage(result) || 'SOAP Fault in der Antwort',
      httpCode,
      portalSuccess: interpreted?.portalSuccess ?? null,
      hints,
    };
  }

  if (interpreted?.portalError && (interpreted.portalError.code != null || interpreted.portalError.message)) {
    const errCode = interpreted.portalError.code;
    // Code 200 vom Portal = Erfolg
    if (isRollerPortalSuccessCode(errCode) || interpreted.portalSuccess === true) {
      return {
        code: isRollerPortalSuccessCode(errCode) ? String(errCode) : 'OK',
        message: String(interpreted.portalError.message ?? '').trim()
          || 'Roller API Antwort empfangen',
        httpCode,
        portalSuccess: true,
        auftraegeCount: interpreted.auftraegeCount ?? null,
        hints,
      };
    }

    const hasItems = soapResponseHasOrderItems(result);
    if (hasItems) {
      const count = interpreted.auftraegeCount;
      return {
        code: 'OK',
        message: count != null
          ? `${count} Auftragsposition(en) empfangen`
          : 'Auftragsdaten in SOAP-Body (items) gefunden',
        httpCode,
        portalSuccess: true,
        auftraegeCount: count ?? null,
        hints,
      };
    }

    return {
      code: String(interpreted.portalError.code ?? httpCode ?? '?'),
      message: String(interpreted.portalError.message ?? '').trim()
        || getSoapExecuteErrorMessage(result)
        || 'Portal-Fehler ohne Meldung',
      httpCode,
      portalSuccess: interpreted.portalSuccess ?? false,
      auftraegeCount: interpreted.auftraegeCount ?? null,
      hints,
    };
  }

  if (result.error && !isRollerPortalSuccessCode(httpCode) && interpreted?.portalSuccess !== true) {
    return {
      code: String(httpCode ?? 'ERR'),
      message: result.error,
      httpCode,
      portalSuccess: interpreted?.portalSuccess ?? null,
      hints,
    };
  }

  const importStats = interpreted?.importStats;
  if (importStats && (importStats.linesCorrect != null || importStats.linesIncorrct != null)) {
    const correct = importStats.linesCorrect ?? 0;
    const incorrect = importStats.linesIncorrct ?? 0;
    const ok =
      interpreted?.portalSuccess !== false ||
      isRollerPortalSuccessCode(httpCode);
    return {
      code: ok ? (isRollerPortalSuccessCode(httpCode) ? String(httpCode) : 'OK') : String(httpCode ?? 'FAIL'),
      message: `Import: ${correct} korrekt, ${incorrect} fehlerhaft`,
      httpCode,
      portalSuccess: ok ? true : interpreted?.portalSuccess ?? null,
      hints,
    };
  }

  const count = interpreted?.auftraegeCount;
  const portalSuccess = interpreted?.portalSuccess;
  const hasItems = soapResponseHasOrderItems(result);
  let code = portalSuccess === true || hasItems ? 'OK' : String(httpCode ?? '200');
  if (isRollerPortalSuccessCode(httpCode) || isRollerPortalSuccessCode(code)) {
    code = String(httpCode ?? code ?? '200');
  }
  if (portalSuccess === false && !hasItems && !isRollerPortalSuccessCode(httpCode)) {
    code = String(interpreted?.portalError?.code ?? httpCode ?? 'FAIL');
  }

  const effectiveSuccess =
    hasItems ||
    portalSuccess === true ||
    isRollerPortalSuccessCode(code) ||
    isRollerPortalSuccessCode(httpCode);

  return {
    code,
    message: count != null
      ? `${count} Auftragsposition(en) empfangen`
      : hasItems
        ? 'Auftragsdaten in SOAP-Body (items) gefunden'
        : (getSoapExecuteErrorMessage(result) || 'Roller API Antwort empfangen'),
    httpCode,
    portalSuccess: effectiveSuccess ? true : portalSuccess,
    auftraegeCount: count ?? null,
    hints,
  };
}

export function getSoapExecuteErrorMessage(result: SoapExecuteResult): string | null {
  if (result.error) {
    return result.error;
  }

  if (result.fault) {
    const fault = result.fault as Record<string, unknown>;
    const faultString = fault?.faultstring;
    if (typeof faultString === 'string' && faultString.trim()) {
      return faultString;
    }
    return 'SOAP Fault in der Antwort.';
  }

  const interpreted = result.interpreted;
  const hasOrderData = soapResponseHasOrderItems(result);

  if (interpreted?.portalSuccess === false && !hasOrderData) {
    const portalError = interpreted.portalError;
    const code = portalError?.code ?? '?';
    if (isRollerPortalSuccessCode(code)) {
      return null;
    }
    const message = portalError?.message ?? 'ohne Meldung';
    return `Portal-Fehler ${code}: ${message}`;
  }

  return null;
}

export function buildSoapExecutePayload(
  operation: SoapOperation,
  envelope: string,
  endpointOverride?: string
): SoapExecutePayload {
  return {
    endpoint: endpointOverride || ROLLER_SOAP_ENDPOINT,
    soapAction: operation.soapAction || '',
    envelope,
  };
}

export const ROLLER_SOAP_ENDPOINT = endpoint;
